import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import config from '../../config/config';

/**
 * Paylaşılan Intersection Observer (grid performansı)
 */
let sharedObserver = null;
const observerCallbacks = new WeakMap();

const createSharedObserver = () => {
  if (sharedObserver) return sharedObserver;

  sharedObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const callback = observerCallbacks.get(entry.target);
        if (callback && entry.isIntersecting) {
          callback();
          observerCallbacks.delete(entry.target);
          sharedObserver.unobserve(entry.target);
        }
      });
    },
    {
      rootMargin: '120px',
      threshold: 0.01
    }
  );

  return sharedObserver;
};

/**
 * Baskı desen görselleri için optimize edilmiş görüntüleyici.
 * - Yerel /uploads/baski/desenler/... için API thumbnail (disk önbellekli, küçük boyut)
 * - Harici http(s) URL'ler için /api/baski/thumbnail-remote (sunucuda önbellekli küçük görsel)
 * - Görünür alana girince yükleme (IntersectionObserver); tam boy gösterim ayrı akışta (ör. detay modal)
 */
const BaskiGorselComponent = ({
  imageUrl,
  alt,
  priority = false,
  pauseLoading = false,
  onLoad,
  useThumbnail = false,
  thumbnailSize = 300
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isInView, setIsInView] = useState(priority);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const imgRef = useRef(null);
  const imgLoadRef = useRef(null);
  const maxRetries = 2;

  useEffect(() => {
    setLoading(true);
    setError(false);
    setRetryCount(0);
    setThumbnailLoaded(false);
    setThumbnailFailed(false);
    setIsInView(priority);
  }, [imageUrl, priority]);

  useEffect(() => {
    if (priority || !imageUrl) {
      setIsInView(true);
      return;
    }

    if (pauseLoading) {
      return;
    }

    const observer = createSharedObserver();
    const currentRef = imgRef.current;

    if (currentRef) {
      observerCallbacks.set(currentRef, () => {
        if (!pauseLoading) {
          setIsInView(true);
        }
      });
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observerCallbacks.delete(currentRef);
        observer.unobserve(currentRef);
      }
    };
  }, [imageUrl, priority, pauseLoading]);

  const finalUrl = useMemo(() => {
    if (!imageUrl) return null;
    const raw = String(imageUrl).trim();
    if (!raw) return null;
    if (raw.startsWith('http')) return raw;
    const path = raw.startsWith('/') ? raw : `/${raw}`;
    return `${config.uploadsUrl}${path}`;
  }, [imageUrl]);

  const thumbnailUrl = useMemo(() => {
    if (!imageUrl || !finalUrl) return null;
    const size = Math.max(120, Math.min(600, Number(thumbnailSize) || 300));

    try {
      const url = new URL(finalUrl);
      const pathname = url.pathname;

      if (pathname.includes('/baski/desenler/')) {
        const tenantMatch = pathname.match(/\/uploads\/([^/]+)\/baski\/desenler\/(.+)$/);
        if (tenantMatch) {
          const tenantCode = tenantMatch[1];
          const filename = tenantMatch[2];
          return `${url.origin}/api/baski/thumbnail/${encodeURIComponent(tenantCode)}/${filename}?width=${size}&height=${size}&webp=true`;
        }
        const filename = pathname.split('/baski/desenler/')[1];
        if (filename) {
          return `${url.origin}/api/baski/thumbnail/${filename}?width=${size}&height=${size}&webp=true`;
        }
      }

      if (url.protocol === 'http:' || url.protocol === 'https:') {
        if (!pathname.includes('/baski/desenler/')) {
          const apiBase = (config.backendUrl || '').replace(/\/$/, '');
          const q = new URLSearchParams({
            url: finalUrl,
            width: String(size),
            height: String(size),
            webp: 'true'
          });
          return `${apiBase}/api/baski/thumbnail-remote?${q.toString()}`;
        }
      }
    } catch {
      /* ignore */
    }

    return null;
  }, [imageUrl, finalUrl, thumbnailSize]);

  const shouldUseThumbnail = useThumbnail && thumbnailUrl;

  useEffect(() => {
    if (!isInView || !shouldUseThumbnail || !thumbnailUrl) {
      return;
    }

    if (pauseLoading) {
      return;
    }

    const thumbnailImg = new Image();
    thumbnailImg.onload = () => {
      if (pauseLoading) return;
      setThumbnailLoaded(true);
      setLoading(false);
      if (onLoad) {
        onLoad();
      }
    };
    thumbnailImg.onerror = () => {
      if (pauseLoading) return;
      setThumbnailLoaded(false);
      setThumbnailFailed(true);
    };
    thumbnailImg.src = thumbnailUrl;

    return () => {
      thumbnailImg.onload = null;
      thumbnailImg.onerror = null;
      thumbnailImg.src = '';
    };
  }, [isInView, shouldUseThumbnail, thumbnailUrl, pauseLoading, onLoad]);

  useEffect(() => {
    if (!isInView || !finalUrl) {
      return;
    }

    if (pauseLoading) {
      return;
    }

    // Thumbnail modunda: önce küçük görsel; başarılı olursa tam boy indirme
    if (shouldUseThumbnail && thumbnailUrl && !thumbnailFailed) {
      return;
    }

    if (shouldUseThumbnail && thumbnailLoaded) {
      return;
    }

    if (imgLoadRef.current) {
      imgLoadRef.current.onload = null;
      imgLoadRef.current.onerror = null;
      imgLoadRef.current.src = '';
    }

    const img = new Image();
    imgLoadRef.current = img;

    img.onload = () => {
      if (pauseLoading) {
        return;
      }
      setLoading(false);
      setError(false);
      setRetryCount(0);
      if (onLoad) {
        onLoad();
      }
    };

    img.onerror = () => {
      if (pauseLoading) {
        return;
      }
      if (retryCount < maxRetries) {
        setTimeout(() => {
          if (pauseLoading) return;
          setRetryCount((prev) => prev + 1);
          img.src = finalUrl + (finalUrl.includes('?') ? '&' : '?') + `retry=${retryCount + 1}&t=${Date.now()}`;
        }, 1000 * (retryCount + 1));
      } else {
        setError(true);
        setLoading(false);
      }
    };

    img.src = finalUrl;

    return () => {
      if (imgLoadRef.current) {
        imgLoadRef.current.onload = null;
        imgLoadRef.current.onerror = null;
        imgLoadRef.current.src = '';
      }
    };
  }, [isInView, finalUrl, retryCount, pauseLoading, shouldUseThumbnail, thumbnailUrl, thumbnailFailed, thumbnailLoaded, onLoad]);

  if (!imageUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700">
        <ImageIcon className="h-12 w-12 text-gray-400" />
      </div>
    );
  }

  return (
    <div ref={imgRef} className="w-full h-full relative overflow-hidden">
      {loading && isInView && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700 z-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          {retryCount > 0 && (
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              Yeniden deneniyor... ({retryCount}/{maxRetries})
            </span>
          )}
        </div>
      )}

      {isInView && (
        <>
          {shouldUseThumbnail && thumbnailUrl && !thumbnailFailed ? (
            <>
              {thumbnailLoaded && (
                <img
                  className="w-full h-full object-cover"
                  src={thumbnailUrl}
                  alt={alt || ''}
                  loading="eager"
                  decoding="async"
                />
              )}
              {!thumbnailLoaded && loading && (
                <div className="absolute inset-0 bg-gray-200 dark:bg-gray-600 animate-pulse" />
              )}
            </>
          ) : (
            <>
              {thumbnailUrl && loading && !shouldUseThumbnail && (
                <img
                  className="absolute inset-0 w-full h-full object-cover opacity-50"
                  src={thumbnailUrl}
                  alt={alt || ''}
                  loading="eager"
                  decoding="async"
                />
              )}

              {loading && !thumbnailUrl && (
                <div
                  className="absolute inset-0 bg-gray-200 dark:bg-gray-600 animate-pulse"
                  style={{
                    backgroundImage: finalUrl ? `url(${finalUrl})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(10px)',
                    transform: 'scale(1.1)'
                  }}
                />
              )}

              {!pauseLoading && (
                <img
                  className={`w-full h-full object-cover transition-opacity duration-500 ${
                    loading ? 'opacity-0' : 'opacity-100'
                  }`}
                  src={finalUrl}
                  alt={alt || ''}
                  loading={priority ? 'eager' : 'lazy'}
                  decoding="async"
                  fetchpriority={priority ? 'high' : 'auto'}
                  onLoad={() => {
                    if (pauseLoading) return;
                    setLoading(false);
                    setError(false);
                    if (onLoad) {
                      onLoad();
                    }
                  }}
                  onError={() => {
                    if (pauseLoading) return;
                    if (retryCount < maxRetries) {
                      setRetryCount((prev) => prev + 1);
                    } else {
                      setError(true);
                      setLoading(false);
                    }
                  }}
                />
              )}
            </>
          )}
        </>
      )}

      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-700 z-20">
          <ImageIcon className="h-12 w-12 text-gray-400 mb-2" />
          <button
            type="button"
            onClick={() => {
              setError(false);
              setLoading(true);
              setRetryCount(0);
              setIsInView(true);
            }}
            className="mt-2 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Tekrar Dene
          </button>
        </div>
      )}
    </div>
  );
};

export { BaskiGorselComponent };
